import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Tabs, Tab, Button,
} from "@mui/material";
import { listAppointments, getTodayAppointmentsByDoctorID, getUpcomingAppointmentsByDoctorID } from '../../service/appointment';
import Navbar from '../navbar';
import {
  Refresh as RefreshIcon, PlayArrow as PlayIcon,
  Pause as PauseIcon, Timer as TimerIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import AppointmentsTab from './Appointments';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import PatientDetails from './PatientDetails';
import AssessmentDetails from './AssessmentDetails';

const Dashboard = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);

  const [todayAppointments, setTodayAppointments] = useState([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeAppointmentTab, setActiveAppointmentTab] = useState(() => {
    return localStorage.getItem("dashboardAppointmentTab") || "today";
  });
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [search, setSearch] = useState('');
  const [user, setUser] = useState(null);
  const [refreshMenuAnchor, setRefreshMenuAnchor] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(0);
  const [lastApiCallTime, setLastApiCallTime] = useState(null);
  const [now, setNow] = useState(new Date());
  const [messagingOpen, setMessagingOpen] = useState(true);
  const [messagingMinimized, setMessagingMinimized] = useState(true);
  const [activeMainTab, setActiveMainTab] = useState(() => {
    const savedTab = localStorage.getItem("dashboardMainTab");
    return savedTab || "appointments";
  });
  const [assessmentSection, setAssessmentSection] = useState(() => {
    return localStorage.getItem("dashboardAssessmentSection") || "assigned";
  });


  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("dashboardMainTab", activeMainTab);
  }, [activeMainTab]);

  useEffect(() => {
    localStorage.setItem("dashboardAppointmentTab", activeAppointmentTab);
  }, [activeAppointmentTab]);

  useEffect(() => {
    localStorage.setItem("dashboardAssessmentSection", assessmentSection);
  }, [assessmentSection]);

  const fetchAppointments = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const data = await listAppointments({ dr_id: user.id });
      console.log("APPOINTMENTS API DATA:", data);
      setAppointments(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Failed to load appointments');
      setAppointments([]);
    } finally {
      setLoading(false);
      setLastApiCallTime(new Date());
    }
  };

  const fetchTodayAppointments = async () => {
    if (!user?.id) return;

    try {
      const data = await getTodayAppointmentsByDoctorID(user.id);
      console.log("TODAY APPOINTMENTS API DATA:", data);
      setTodayAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching today appointments:', err);
      setTodayAppointments([]);
    }
  };

  const fetchUpcomingAppointments = async () => {
    if (!user?.id) return;

    try {
      const data = await getUpcomingAppointmentsByDoctorID(user.id);
      console.log("UPCOMING APPOINTMENTS API DATA:", data);
      setUpcomingAppointments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching upcoming appointments:', err);
      setUpcomingAppointments([]);
    }
  };

  useEffect(() => {
    fetchAppointments();
    fetchTodayAppointments();
    fetchUpcomingAppointments();
  }, [user?.id]);

  useEffect(() => {
    if (refreshInterval === 0) return; // Manual mode → no auto refresh

    // Call immediately once
    fetchAppointments();
    fetchTodayAppointments();
    fetchUpcomingAppointments();
    setLastApiCallTime(new Date());

    const intervalId = setInterval(() => {
      fetchAppointments();
      fetchTodayAppointments();
      fetchUpcomingAppointments();
      setLastApiCallTime(new Date());
    }, refreshInterval * 1000);

    // Cleanup when interval changes or component unmounts
    return () => clearInterval(intervalId);
  }, [refreshInterval]);

  const today = new Date().toISOString().split('T')[0];

  // const pastAppointmentsCount = React.useMemo(() => {
  //   return appointments.filter(a => {
  //     const date = new Date(a.slot_date).toISOString().split('T')[0];
  //     return date < today;
  //   }).length;
  // }, [appointments, today]);

  const todayCount = todayAppointments.length;

  const upcomingCount = upcomingAppointments.length;

  const draftCount = appointments.filter(a => {
    const appointmentDateTime = new Date(
      `${a.slot_date.split("T")[0]}T${a.end_time}`
    );
    const diffInHours = (new Date() - appointmentDateTime) / (1000 * 60 * 60);
    const status = a.status?.toLowerCase();
    if (!(diffInHours >= 0 && diffInHours <= 72 && (status === "drafted" || status === "scheduled"))) return false;
    if (status === 'cancelled' || status === 'canceled') return false;
    // exclude no-show variants
    const ended = diffInHours >= 0;
    const hasActivity = !!(a.customer_state?.trim() || a.counsellor_state?.trim());
    if (status === 'scheduled' && !hasActivity && ended) return false;
    if (status === 'scheduled' && !a.customer_state?.trim() && !!a.counsellor_state?.trim()) return false;
    if (status === 'scheduled' && !!a.customer_state?.trim() && !a.counsellor_state?.trim()) return false;
    return true;
  }).length;

  useEffect(() => {
    let filtered = [];

    const normalizeDate = (dateStr) =>
      new Date(dateStr).toISOString().split("T")[0];

    if (activeAppointmentTab === "today") {
      const now = new Date();
      // Today API appointments + completed/drafted cases within 0–24 h after session end
      const recentlyCompleted = appointments.filter(a => {
        const endDt = new Date(`${a.slot_date.split("T")[0]}T${a.end_time}`);
        const diffH = (now - endDt) / (1000 * 60 * 60);
        return diffH >= 0 && diffH < 24 &&
          (a.status?.toLowerCase() === "drafted" || a.status?.toLowerCase() === "completed");
      });
      // Merge, deduplicate by id
      const merged = [...todayAppointments];
      recentlyCompleted.forEach(rc => {
        if (!merged.find(m => m.id === rc.id)) merged.push(rc);
      });
      filtered = merged;
    } else if (activeAppointmentTab === "upcoming") {
      filtered = upcomingAppointments;
    } else if (activeAppointmentTab === "draft") {
      const now = new Date();

      filtered = appointments.filter(a => {
        const appointmentDateTime = new Date(
          `${a.slot_date.split("T")[0]}T${a.end_time}`
        );
        const diffInHours = (now - appointmentDateTime) / (1000 * 60 * 60);
        const status = a.status?.toLowerCase();
        // Draft tab: session ended within 72h and not yet completed/cancelled
        if (!(diffInHours >= 0 && diffInHours <= 72 && (status === "drafted" || status === "scheduled"))) return false;
        if (status === 'cancelled' || status === 'canceled') return false;
        const ended = diffInHours >= 0;
        const hasActivity = !!(a.customer_state?.trim() || a.counsellor_state?.trim());
        if (status === 'scheduled' && !hasActivity && ended) return false;
        if (status === 'scheduled' && !a.customer_state?.trim() && !!a.counsellor_state?.trim()) return false;
        if (status === 'scheduled' && !!a.customer_state?.trim() && !a.counsellor_state?.trim()) return false;
        return true;
      });
    }

    // 🔍 SEARCH FILTER
    if (search.trim()) {
      const q = search.toLowerCase();

      filtered = filtered.filter(a =>
        a.customer_name?.toLowerCase().includes(q) ||
        a.customer_email?.toLowerCase().includes(q) ||
        a.consultation_type?.toLowerCase().includes(q) ||
        a.status?.toLowerCase().includes(q)
      );
    }

    setFilteredAppointments(filtered);
  }, [activeAppointmentTab, appointments, today, search, todayAppointments, upcomingAppointments]);

  const CATEGORY_COLOR = '#0891b2';

  const refreshOptions = [
    { label: 'Manual', value: 0, icon: <PauseIcon fontSize="small" /> },
    { label: '10 seconds', value: 10, icon: <TimerIcon fontSize="small" /> },
    { label: '30 seconds', value: 30, icon: <TimerIcon fontSize="small" /> },
    { label: '1 minute', value: 60, icon: <TimerIcon fontSize="small" /> },
    { label: '5 minutes', value: 300, icon: <TimerIcon fontSize="small" /> },
  ];

  useEffect(() => {
    const ticker = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(ticker);
  }, []);

  const formatLastUpdated = (date) => {
    if (!date) return '—';
    const diffSec = Math.floor((now - date) / 1000);
    if (diffSec < 10) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min ago`;
    return date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10));
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusStyles = (status) => {
    switch (status?.toLowerCase()) {
      case 'scheduled':
        return {
          color: '#1d4ed8',
          backgroundColor: '#eff6ff',
          borderColor: '#bfdbfe'
        };
      case 'cancelled':
      case 'canceled':
        return {
          color: '#b91c1c',
          backgroundColor: '#fef2f2',
          borderColor: '#fecaca'
        };
      case 'completed':
        return {
          color: '#15803d',
          backgroundColor: '#f0fdf4',
          borderColor: '#bbf7d0'
        };
      case 'drafted':
        return {
          color: '#d97706',
          backgroundColor: '#fffbeb',
          borderColor: '#fde68a',
        };
      case 'not_attended':
        return {
          color: '#b91c1c',
          backgroundColor: '#fee2e2',
          borderColor: '#fecaca',
        };
      default:
        return {
          color: '#475569',
          backgroundColor: '#f1f5f9',
          borderColor: '#e2e8f0',
        };
    }
  };

  const handleAppointmentClick = (apt) => {
    if (!apt?.customer_id || !apt?.id) return;
    window.location.href = `/patient/view/${apt.customer_id}/${apt.id}`;
  };

  const getPatientInfo = (apt) => {
    const name = apt.customer_name || 'Unknown';

    return {
      name,
      initials: name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(),
      id: apt.customer_id,
      email: apt.customer_email
    };
  };

  const handleManualRefresh = async () => {
    await fetchAppointments();
    await fetchTodayAppointments();
    await fetchUpcomingAppointments();
    setLastApiCallTime(new Date());
  };

  const handleMessagingClick = () => {
    setMessagingOpen(true);
    setMessagingMinimized(false);
  };

  const handleMessagingClose = () => {
    setMessagingOpen(false);
    setMessagingMinimized(false);
  };

  const handleMessagingMinimize = () => {
    setMessagingMinimized(!messagingMinimized);
  };

  return (

    <Box sx={{ minHeight: "100vh", background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)', }}>
      <Navbar onMessagingClick={handleMessagingClick} />

      {/* Top Navigation Tabs */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #e5e7eb",
          position: "sticky",
          top: 70,
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
        }}
      >
        <Tabs
          value={activeMainTab}
          onChange={(_, newValue) => setActiveMainTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.9rem",
              minHeight: 48,
            },
            "& .MuiTab-wrapper": {
              flexDirection: "row",
              gap: 1,
            },
          }}
        >
          <Tab
            value="appointments"
            icon={<CalendarTodayIcon sx={{ fontSize: "1.1rem" }} />}
            iconPosition="start"
            label="Appointments"
          />
          <Tab
            value="assessments"
            icon={<AssignmentIcon sx={{ fontSize: "1.1rem" }} />}
            iconPosition="start"
            label="Assessments"
          />
          <Tab
            value="patient-details"
            icon={<PersonIcon sx={{ fontSize: "1.1rem" }} />}
            iconPosition="start"
            label="Patient Details"
          />
        </Tabs>
        {activeMainTab === "assessments" && (
          <Box sx={{ display: "flex", bgcolor: "#f1f5f9", borderRadius: "8px", p: 0.35, gap: 0.35 }}>
            <Button size="small" onClick={() => setAssessmentSection("assigned")}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderRadius: "6px", px: 1.25, py: 0.4, minWidth: 0, bgcolor: assessmentSection === "assigned" ? "#fff" : "transparent", color: assessmentSection === "assigned" ? "#111827" : "#6b7280", boxShadow: assessmentSection === "assigned" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", "&:hover": { bgcolor: assessmentSection === "assigned" ? "#fff" : "#e2e8f0" } }}>
              Assigned
            </Button>
            <Button size="small" onClick={() => setAssessmentSection("library")}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: "0.75rem", borderRadius: "6px", px: 1.25, py: 0.4, minWidth: 0, bgcolor: assessmentSection === "library" ? "#fff" : "transparent", color: assessmentSection === "library" ? "#111827" : "#6b7280", boxShadow: assessmentSection === "library" ? "0 1px 3px rgba(0,0,0,0.1)" : "none", "&:hover": { bgcolor: assessmentSection === "library" ? "#fff" : "#e2e8f0" } }}>
              Library
            </Button>
          </Box>
        )}
      </Box>

      {/* Header - only show for appointments */}
      {activeMainTab === "appointments" && (
        <Box
          sx={{
            bgcolor: "white",
            px: 4,
            pt: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 3,
            background: 'linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)',
          }}
        >
          {/* LEFT SIDE */}
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontSize: "100%", mb: 1 }}
            >
              Manage schedules and bookings
            </Typography>
            {/* <TextField
            variant="outlined"
            size="small"
            placeholder="Search appointments..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9ca3af' }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 2,
                backgroundColor: '#fff',
                
                '& fieldset': {
                  borderColor: '#e2e8f0',
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#8b5cf6',
                  
                  borderWidth: '2px',
                },
              },
            }}
            sx={{ width: 300 }}
          /> */}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, flexDirection: "column", }}>



            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>

              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Last updated: {formatLastUpdated(lastApiCallTime)}
              </Typography>
              <Box sx={{
                display: 'flex',
                bgcolor: '#f8fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
              }}>
                <Tooltip title="Refresh now">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleManualRefresh}
                      disabled={loading}
                      sx={{
                        borderRadius: 0,
                        px: 1.5,
                        '&:hover': { bgcolor: '#e2e8f0' }
                      }}
                    >
                      <RefreshIcon sx={{
                        fontSize: 18,
                        color: loading ? 'grey.400' : CATEGORY_COLOR,
                        animation: loading ? 'spin 1s linear infinite' : 'none',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' }
                        }
                      }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    px: 1.5,
                    py: 0.75,
                    borderLeft: '1px solid #e2e8f0',
                    '&:hover': { bgcolor: '#e2e8f0' }
                  }}
                  onClick={(e) => setRefreshMenuAnchor(e.currentTarget)}
                >
                  {refreshInterval === 0 ? (
                    <PauseIcon sx={{ fontSize: 14, color: '#64748b', mr: 0.5 }} />
                  ) : (
                    <PlayIcon sx={{ fontSize: 14, color: '#22c55e', mr: 0.5 }} />
                  )}
                  <Typography variant="caption" sx={{ color: '#475569', fontWeight: 500 }}>
                    {refreshInterval === 0 ? 'Manual' : refreshOptions.find(o => o.value === refreshInterval)?.label}
                  </Typography>
                </Box>
              </Box>

              {/* Refresh Interval Menu */}
              <Menu
                anchorEl={refreshMenuAnchor}
                open={Boolean(refreshMenuAnchor)}
                onClose={() => setRefreshMenuAnchor(null)}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 160, mt: 1 } }}
              >
                {refreshOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={() => { setRefreshInterval(option.value); setRefreshMenuAnchor(null); }}
                    selected={refreshInterval === option.value}
                    sx={{ py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>{option.icon}</ListItemIcon>
                    <ListItemText primary={option.label} primaryTypographyProps={{ variant: 'body2' }} />
                  </MenuItem>
                ))}
              </Menu>

            </Box>


          </Box>

        </Box>
      )}

      {/* Top Navigation Tabs */}


      {/* Content */}
      {/* Content */}
      <Box
        sx={{
          mx: 4,
          py: 3,
          minHeight: "500px",
          background:
            "linear-gradient(135deg, #f8fffe 0%, #f5f3ff 100%)",
        }}
      >
        {activeMainTab === "appointments" && (
          <>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
              </Box>
            ) : (
              <AppointmentsTab
                appointments={appointments}
                loading={loading}
                activeAppointmentTab={activeAppointmentTab}
                setActiveAppointmentTab={setActiveAppointmentTab}
                // pastAppointmentsCount={pastAppointmentsCount}
                todayCount={todayCount}
                upcomingCount={upcomingCount}
                filteredAppointments={filteredAppointments}
                allAppointments={appointments}
                draftCount={draftCount}
                fetchAppointments={fetchAppointments}
                fetchTodayAppointments={fetchTodayAppointments}
                fetchUpcomingAppointments={fetchUpcomingAppointments}
              />
            )}
          </>
        )}

        {activeMainTab === "patient-details" && (
          <PatientDetails
            appointments={appointments}
            loading={loading}
            today={today}
            getPatientInfo={getPatientInfo}
            handleAppointmentClick={handleAppointmentClick}
            formatDate={formatDate}
            formatTime={formatTime}
            getStatusStyles={getStatusStyles}
          />
        )}

        {activeMainTab === "assessments" && (
          <AssessmentDetails hideNavbar hideOuterWrapper mainSection={assessmentSection} setMainSection={setAssessmentSection} />
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
